import { useMemo } from 'react';
import { InstanceRowActions } from '../components/InstanceRowActions';
import { TruncatedCell } from '../../../components/ui/tableTemplate/TruncatedCell';
import { cellSx } from '../../../components/ui/tableTemplate/cellRenderers';
import type { TableColumn } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { COLUMN_WIDTH } from '../../../shared/constants';
import { SyncStatusIcon } from '../../../components/commons/syncStatus/SyncStatusIcon';
import { KeyStatusBadge } from '../components/registrationKey/KeyStatusBadge';
import { TerrainChips } from '../components/TerrainChips';
import { REGISTRATION_KEY_STATUS, type InstanceRow, type Terrain } from '../instanceTypes';
import { formatDate } from '../../../lib/date/formatDate';

const I18N_PREFIX = 'instances:columns';

interface UseInstanceColumnsOptions {
  terrainOptions: string[];
  terrainMap?: Map<string, Terrain>;
  onEdit: (row: InstanceRow) => void;
  onGenerateKey: (row: InstanceRow) => void;
  onDelete: (row: InstanceRow) => void;
  generatingKeyId?: string | null;
  /** Rights on the `instances` line (ADR-040), forwarded to the row actions. */
  canModify: boolean;
  canDelete: boolean;
}

export function useInstanceColumns({
  terrainOptions,
  terrainMap,
  onEdit,
  onGenerateKey,
  onDelete,
  generatingKeyId,
  canModify,
  canDelete,
}: UseInstanceColumnsOptions): TableColumn<InstanceRow>[] {
  return useMemo(
    () => [
      {
        key: 'registeredAt' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.sync`,
        databaseName: 'registered_at',
        width: COLUMN_WIDTH.icon,
        sortable: true,
        type: 'icon',
        align: 'center',
        render: (row: InstanceRow) => {
          // A revoked current key is surfaced here as a dedicated instance-status
          // icon, derived client-side — the stored `status` is unchanged.
          const displayStatus =
            row.keyStatus === REGISTRATION_KEY_STATUS.REVOKED ? 'revoked' : row.status;
          return <SyncStatusIcon status={displayStatus} fontSize="medium" />;
        },
      },
      {
        key: 'name' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.instanceName`,
        databaseName: 'instance_name',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        render: (row: InstanceRow) => <TruncatedCell text={row.name || '—'} sx={cellSx} />,
      },
      {
        key: 'keyStatus',
        labelKey: `${I18N_PREFIX}.keyStatus`,
        width: COLUMN_WIDTH.text,
        render: (row: InstanceRow) => <KeyStatusBadge status={row.keyStatus} />,
      },
      {
        key: 'computerName' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.computerName`,
        databaseName: 'computer_name',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        render: (row: InstanceRow) => <TruncatedCell text={row.computerName ?? '—'} sx={cellSx} />,
      },
      {
        key: 'email' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.email`,
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        render: (row: InstanceRow) => <TruncatedCell text={row.email ?? '—'} sx={cellSx} />,
      },
      {
        key: 'pays' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.pays`,
        databaseName: 'pays',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        render: (row: InstanceRow) => <TruncatedCell text={row.pays ?? '—'} sx={cellSx} />,
      },
      {
        key: 'codeterrain' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.terrains`,
        width: COLUMN_WIDTH.multiValue,
        filter: { kind: 'list', options: terrainOptions },
        render: (row: InstanceRow) => <TerrainChips codes={row.codeterrain} maxVisible={1} />,
      },
      {
        key: 'version' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.version`,
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        render: (row: InstanceRow) => (
          <TruncatedCell text={row.version ?? '—'} sx={{ ...cellSx, whiteSpace: 'nowrap' }} />
        ),
      },
      {
        key: 'genVersion' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.genVersion`,
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        render: (row: InstanceRow) => (
          <TruncatedCell text={row.genVersion ?? '—'} sx={{ ...cellSx, whiteSpace: 'nowrap' }} />
        ),
      },
      {
        key: 'path' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.path`,
        width: COLUMN_WIDTH.text,
        filter: { kind: 'text' },
        render: (row: InstanceRow) => <TruncatedCell text={row.path ?? '—'} sx={cellSx} />,
      },
      {
        key: 'updatedAt' as keyof InstanceRow,
        labelKey: `${I18N_PREFIX}.updatedAt`,
        databaseName: 'updated_at',
        width: COLUMN_WIDTH.date,
        sortable: true,
        filter: { kind: 'date' },
        render: (row: InstanceRow) => {
          const text = row.updatedAt ? formatDate(row.updatedAt, 'dd/MM/yyyy') || '—' : '—';
          return <TruncatedCell text={text} sx={{ ...cellSx, whiteSpace: 'nowrap' }} />;
        },
      },
      {
        key: 'actions' as unknown as keyof InstanceRow,
        labelKey: '',
        width: COLUMN_WIDTH.action,
        stickyRight: true,
        render: (row: InstanceRow) => (
          <InstanceRowActions
            row={row}
            isGenerating={generatingKeyId === row.id}
            canModify={canModify}
            canDelete={canDelete}
            onEdit={onEdit}
            onGenerateKey={onGenerateKey}
            onDelete={onDelete}
          />
        ),
      },
    ],
    [terrainOptions, terrainMap, onEdit, onGenerateKey, onDelete, generatingKeyId, canModify, canDelete],
  );
}
